import React from 'react'
import { path, pathOr } from 'ramda'
import PropTypes from 'prop-types'
import { propType } from 'graphql-anywhere'
import Section from '../../shared/settings/section'
import SubscriptionPlan from './subscription-plan'
import Vouchers from './vouchers'
import PaymentDetails from './payment-details'
import {
  SUBSCRIPTION_STATUS,
  EV_SUBSCRIPTION_STATUS
} from '../../../constants'
import SUBSCRIPTION_INFORMATION_FRAGMENT from '../../../../graphql/fragments/subscription-information.gql'
import { TESTERS } from '../../../lib/tester-classnames'
import DcbSubscription from './dcb-subscription'
import BridingOfferPromotion from './bridging-offer-promotion'

function SubscriptionAndPayment({
  activeSubscription,
  handleUpdateSubscription
}) {
  const recurringVouchers = pathOr(
    [],
    ['recurringVouchers']
  )(activeSubscription)
  const isSuspended =
    path(['status'], activeSubscription) === SUBSCRIPTION_STATUS.SUSPENDED
  const renewalStatus = pathOr('', ['renewalStatus'])(activeSubscription)

  const dcbSubscriptionInfo = path(['dcbSubscriptionInfo'], activeSubscription)

  const isBridgingOfferPromotion = pathOr(
    false,
    ['currentSubscription', 'promotion', 'isBridgingOfferPromotion'],
    activeSubscription
  )

  // My Plan shows one section: subscription plan, DCB subscription, or bridging offer promotion
  const subscriptionPlanVisible =
    !dcbSubscriptionInfo && !isBridgingOfferPromotion
  const dcbSubscriptionVisible =
    !!dcbSubscriptionInfo && !isBridgingOfferPromotion
  const bridgingOfferPromotionVisible = isBridgingOfferPromotion

  // Vouchers are not shown for suspended, DCB, or bridging offer subscriptions
  const vouchersVisible =
    !isSuspended && !dcbSubscriptionInfo && !isBridgingOfferPromotion

  const {
    currentSubscription,
    upcomingSubscription,
    upcomingFinalBillSubscription
  } = activeSubscription
  const nextSubscription =
    upcomingSubscription || upcomingFinalBillSubscription
  const evSubscriptionStatus = path(
    ['evSubscriptionStatus'],
    currentSubscription
  )
  const evUpcomingSubscriptionStatus = path(
    ['evSubscriptionStatus'],
    nextSubscription
  )

  // Use Evergent final bill status to decide if the subscription is cancelled
  const isCancelled =
    evSubscriptionStatus === EV_SUBSCRIPTION_STATUS.FINAL_BILL &&
    (!evUpcomingSubscriptionStatus ||
      evUpcomingSubscriptionStatus === EV_SUBSCRIPTION_STATUS.FINAL_BILL)

  // Do not allow vouchers when cancelling after downgrading
  // and both current and upcoming subscriptions are in final bill
  const voucherNotAllowed =
    vouchersVisible &&
    evSubscriptionStatus === EV_SUBSCRIPTION_STATUS.FINAL_BILL &&
    evUpcomingSubscriptionStatus === EV_SUBSCRIPTION_STATUS.FINAL_BILL

  const voucherMessage = voucherNotAllowed
    ? `Sorry, you can't use a voucher at this time`
    : ''

  return (
    <>
      {subscriptionPlanVisible && (
        <Section title="My Plan" testerClassName={TESTERS.ACCOUNT.TV_PLAN}>
          <SubscriptionPlan
            subscription={activeSubscription}
            handleUpdateSubscription={handleUpdateSubscription}
            recurringVouchers={recurringVouchers}
            renewalStatus={renewalStatus}
            isCancelled={isCancelled}
          />
        </Section>
      )}

      {dcbSubscriptionVisible && (
        <Section title="My Plan">
          <DcbSubscription dcbSubscriptionInfo={dcbSubscriptionInfo} />
        </Section>
      )}

      {bridgingOfferPromotionVisible && (
        <Section title="My Plan">
          <BridingOfferPromotion />
        </Section>
      )}

      {vouchersVisible && (
        <Section title="Vouchers">
          <Vouchers
            recurringVouchers={recurringVouchers}
            renewalStatus={renewalStatus}
            addVoucherVisible={!voucherNotAllowed}
            message={voucherMessage}
          />
        </Section>
      )}

      <Section
        title="Payment Details"
        testerClassName={TESTERS.ACCOUNT.PAYMENT_DETAILS}
      >
        <PaymentDetails activeSubscription={activeSubscription} />
      </Section>
    </>
  )
}

SubscriptionAndPayment.propTypes = {
  activeSubscription: propType(SUBSCRIPTION_INFORMATION_FRAGMENT).isRequired,
  handleUpdateSubscription: PropTypes.func.isRequired
}

export default SubscriptionAndPayment
